import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, filter, map, tap } from 'rxjs/operators';

import { ActieveConfiguratie, KanaalconfiguratieBeheerResource } from '@ap/interface-apic-kanaalconfiguratie-beheer';

import { BooleanConfigurations, ChannelName, JSONConfiguration } from './mijn-kanaalconfiguratie.model';

@Injectable({ providedIn: 'root' })
export class MijnKanaalconfiguratieService {
    private readonly cacheMap = new Map();

    public constructor(private readonly kanaalconfiguratieBeheerResource: KanaalconfiguratieBeheerResource) {}

    private static mapConfiguratiesToBooleanConfigurations<T>(configuraties: ActieveConfiguratie[]): T {
        return configuraties.reduce((booleanConfigurations: T, currentConfiguration) => {
            if (/true|false/.test(currentConfiguration.waarde)) {
                booleanConfigurations[currentConfiguration.naam] = currentConfiguration.waarde === 'true';
            }
            return booleanConfigurations;
        }, {} as T);
    }

    /**
     * @ngdoc method
     * @name MijnKanaalconfiguratieService#getBooleanConfiguration
     * @methodOf MijnKanaalconfiguratieService
     *
     * @description               Get the boolean value from a configuration, returns false when an error occurs
     * @param channelName         The name of the channel
     * @param configurationName   The name of the configuration
     * @param [useCache=true]     Whether to use cache
     */
    public getBooleanConfiguration(channelName: ChannelName, configurationName: string, useCache = true): Observable<boolean> {
        const cacheKey = `booleanConfiguration-${channelName}-${configurationName}`;
        if (useCache && this.cacheMap.has(cacheKey)) {
            return of(this.cacheMap.get(cacheKey));
        }

        return this.kanaalconfiguratieBeheerResource.geefActieveConfiguratie(channelName, configurationName).pipe(
            map((configuratie) => configuratie.waarde === 'true'),
            tap((configuration) => this.cacheMap.set(cacheKey, configuration)),
            catchError(() => of(false))
        );
    }

    /**
     * @ngdoc method
     * @name MijnKanaalconfiguratieService#getBooleanConfigurations
     * @methodOf MijnKanaalconfiguratieService
     *
     * @description                   Get all the boolean values from a channel
     * @param channelNames            The name(s) of the channel(s), must at least contain 1 channel name
     * @param configurationNames      The name(s) of the configuration(s), optional. Without configuration name the whole channel will be requested
     * @param defaultConfigurations   The default configuration that will be returned when there are no configurations found or when an error occurs
     * @param useCache                Whether to use cache
     */
    public getBooleanConfigurations<T extends BooleanConfigurations>(
        channelNames: ChannelName[],
        configurationNames: string[],
        defaultConfigurations: T,
        useCache = true
    ): Observable<T> {
        const cacheKey = `booleanConfigurations-${channelNames.join(',')}-${configurationNames.join(',')}`;
        if (useCache && this.cacheMap.has(cacheKey)) {
            return of(this.cacheMap.get(cacheKey));
        }

        return this.kanaalconfiguratieBeheerResource.geefActieveConfiguraties(channelNames, configurationNames).pipe(
            filter((lijstVanActieveConfiguraties) => lijstVanActieveConfiguraties.configuraties?.length > 0),
            map((configuraties) => MijnKanaalconfiguratieService.mapConfiguratiesToBooleanConfigurations<T>(configuraties.configuraties)),
            tap((configurations) => this.cacheMap.set(cacheKey, configurations)),
            defaultIfEmpty(defaultConfigurations),
            catchError(() => of(defaultConfigurations))
        );
    }

    /**
     * @ngdoc method
     * @name MijnKanaalconfiguratieService#getJSONConfiguration
     * @methodOf MijnKanaalconfiguratieService
     *
     * @description                     Get a single JSON value from a channel
     * @param channelName               The name of the channel
     * @param configurationName         The name of the configuration
     * @param defaultConfiguration      The default configuration that will be returned when there are no configurations found or when an error occures
     * @param [useCache=true]           Whether to use cache
     */
    public getJSONConfiguration<T extends JSONConfiguration>(
        channelName: ChannelName,
        configurationName: string,
        defaultConfiguration: T,
        useCache = true
    ): Observable<T> {
        const cacheKey = `JSONConfiguration-${channelName}-${configurationName}`;
        if (useCache && this.cacheMap.get(cacheKey)) {
            return of(this.cacheMap.get(cacheKey));
        }

        return this.kanaalconfiguratieBeheerResource.geefActieveConfiguratie(channelName, configurationName).pipe(
            map((configuratie) => (configuratie.waarde ? JSON.parse(configuratie.waarde) : defaultConfiguration)),
            tap((configuration) => this.cacheMap.set(cacheKey, configuration)),
            catchError(() => of(defaultConfiguration))
        );
    }
}
